import { ElevatorPreviewContent } from '~/components/compliance/ElevatorPreviewPanel';
import { ContractPreviewContent } from '~/components/contractReview/ContractPreviewPanel';
import { InvoicePreviewContent } from '~/components/invoices/InvoicePreviewPanel';
import { RowPreviewPanel } from '~/components/rowPreview/RowPreviewPanel';
import type { RowPreview } from '~/components/rowPreview/useRowPreview';
import type { ActionItem } from '~/data/schemas/actionItem';

import { isWorkQueueDialogTarget, type WorkQueueDetailTarget } from './WorkQueueDetailDialog';

// A task opens in its dialog instead; the whole task view does not fit the side panel.
type PreviewTarget = WorkQueueDetailTarget & {
  type: Exclude<WorkQueueDetailTarget['type'], 'task'>;
};

export const previewTarget = (item: ActionItem | undefined): PreviewTarget | null => {
  const type = item?.target?.type;
  if (!item?.target || !type || !isWorkQueueDialogTarget(type) || type === 'task') return null;
  return { type, id: item.target.id };
};

function TargetContent({ target }: { target: PreviewTarget }) {
  switch (target.type) {
    case 'invoice':
      return <InvoicePreviewContent invoiceId={target.id} />;
    case 'contract':
      return <ContractPreviewContent contractId={target.id} />;
    case 'compliance':
      return <ElevatorPreviewContent carId={target.id} />;
  }
}

/** Rows are action items; the panel shows the invoice, contract or elevator each points at. */
export function WorkQueuePreviewPanel({
  rowPreview,
  getItem,
  onOpenItem,
}: {
  rowPreview: RowPreview;
  getItem: (rowId: string) => ActionItem | undefined;
  onOpenItem: (item: ActionItem) => void;
}) {
  return (
    <RowPreviewPanel
      rowPreview={rowPreview}
      ariaLabel="Needs Attention preview"
      onOpenFullPage={(rowId) => {
        const item = getItem(rowId);
        if (item) onOpenItem(item);
      }}
    >
      {(rowId) => {
        const target = previewTarget(getItem(rowId));
        return target ? (
          <TargetContent key={`${target.type}:${target.id}`} target={target} />
        ) : null;
      }}
    </RowPreviewPanel>
  );
}
